import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Pencil, Trash2, Upload, Loader2 } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

export const inputCls =
  "w-full bg-ink border border-paper/20 focus:border-crimson rounded-md px-3 py-2.5 font-ui text-sm text-paper placeholder:text-paper/30 outline-none transition-colors duration-300";

export function Field({ label, hint, children }) {
  return (
    <div className="mb-4">
      <label className="block font-ui text-[11px] uppercase tracking-[0.2em] text-paper/50 mb-2">{label}</label>
      {children}
      {hint && <p className="font-ui text-xs text-paper/35 mt-1.5">{hint}</p>}
    </div>
  );
}

export function SaveBtn({ onClick, saving, label = "Save changes", tid }) {
  return (
    <button onClick={onClick} disabled={saving} data-testid={tid}
      className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.2em] font-semibold bg-crimson text-paper px-6 py-3 rounded-full transition-[background-color,opacity] duration-300 hover:bg-crimsonhi disabled:opacity-50">
      {saving && <Loader2 size={14} className="animate-spin" />}
      {saving ? "Saving…" : label}
    </button>
  );
}

export function UploadButton({ accept, onUploaded, label = "Upload file", tid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = () => ref.current?.click();
  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd);
      onUploaded(r.data.url);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={onChange} data-testid={`${tid}-input`} />
      <button type="button" onClick={pick} disabled={busy} data-testid={tid}
        className="inline-flex items-center gap-2 font-ui text-[11px] uppercase tracking-[0.2em] border border-paper/25 text-paper/80 px-5 py-2.5 rounded-full transition-colors duration-300 hover:border-paper/60 disabled:opacity-50">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {label}
      </button>
    </>
  );
}

export function StatusPill({ status }) {
  const live = status === "published" || status === "available";
  return (
    <span className={`font-ui text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border ${
      live ? "border-gold/40 text-gold" : "border-paper/20 text-paper/40"}`}>
      {status}
    </span>
  );
}

export function CrudTab({ name, idField, title, fields, orderable = false, renderMeta }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/${name}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ status: "draft" });
  const save = async () => {
    setSaving(true);
    try {
      if (editing[idField]) {
        await api.put(`/admin/${name}/${editing[idField]}`, editing);
      } else {
        await api.post(`/admin/${name}`, editing);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (item) => {
    if (!window.confirm("Delete this item permanently?")) return;
    await api.delete(`/admin/${name}/${item[idField]}`);
    toast.success("Deleted");
    load();
  };
  const toggleStatus = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    await api.put(`/admin/${name}/${item[idField]}`, { status: next });
    load();
  };
  const move = async (item, direction) => {
    await api.post(`/admin/${name}/${item[idField]}/move`, { direction });
    load();
  };

  const renderField = (f) => {
    const val = editing?.[f.key] ?? "";
    const setVal = (v) => setEditing({ ...editing, [f.key]: v });
    if (f.type === "textarea") {
      return <textarea rows={f.rows || 4} value={val} onChange={(e) => setVal(e.target.value)} className={`${inputCls} resize-y`} data-testid={`edit-${f.key}`} />;
    }
    if (f.type === "select") {
      return (
        <select value={val} onChange={(e) => setVal(e.target.value)} className={inputCls} data-testid={`edit-${f.key}`}>
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (f.type === "bool") {
      return (
        <label className="inline-flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={!!editing?.[f.key]} onChange={(e) => setVal(e.target.checked)}
            className="accent-[#B71F32] w-4 h-4" data-testid={`edit-${f.key}`} />
          <span className="font-ui text-sm text-paper/70">{f.onLabel || "Enabled"}</span>
        </label>
      );
    }
    if (f.type === "datetime") {
      return <input type="datetime-local" value={val ? String(val).slice(0, 16) : ""} onChange={(e) => setVal(e.target.value)} className={inputCls} data-testid={`edit-${f.key}`} />;
    }
    if (f.type === "number") {
      return <input type="number" value={val ?? ""} onChange={(e) => setVal(e.target.value === "" ? null : Number(e.target.value))} className={inputCls} data-testid={`edit-${f.key}`} />;
    }
    if (f.type === "image" || f.type === "video") {
      return (
        <div className="flex items-center gap-4 flex-wrap">
          <UploadButton accept={f.type === "image" ? "image/*" : "video/*"} onUploaded={setVal}
            label={val ? "Replace file" : f.type === "image" ? "Upload image" : "Upload video"} tid={`upload-${f.key}`} />
          {val && f.type === "image" && <img src={assetUrl(val)} alt="preview" className="h-16 w-auto border border-paper/15" />}
          {val && f.type === "video" && <span className="font-ui text-xs text-paper/50 break-all">{val}</span>}
        </div>
      );
    }
    return <input type={f.type || "text"} value={val} onChange={(e) => setVal(e.target.value)} className={inputCls} data-testid={`edit-${f.key}`} />;
  };

  return (
    <div data-testid={`admin-tab-${name}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl text-paper">{title}</h2>
        <button onClick={openNew} data-testid={`${name}-new-btn`}
          className="font-ui text-[11px] uppercase tracking-[0.2em] font-semibold bg-crimson text-paper px-5 py-2.5 rounded-full transition-colors duration-300 hover:bg-crimsonhi">
          New
        </button>
      </div>
      {items === null ? (
        <p className="font-ui text-sm text-paper/40">Loading…</p>
      ) : items.length === 0 ? (
        <p className="font-ui text-sm text-paper/40 border border-paper/10 p-8" data-testid={`${name}-empty`}>
          Nothing here yet. Create the first entry.
        </p>
      ) : (
        <div className="divide-y divide-paper/10 border-y border-paper/10">
          {items.map((item) => (
            <div key={item[idField]} className="py-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`${name}-row-${item[idField]}`}>
              <div className="min-w-0">
                <p className="font-ui text-sm text-paper truncate">{item.title || item.text || item.name || item[idField]}</p>
                <div className="flex items-center gap-3 mt-1.5">
                  <StatusPill status={item.status} />
                  {renderMeta && <span className="font-ui text-xs text-paper/40">{renderMeta(item)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {orderable && (
                  <>
                    <button onClick={() => move(item, "up")} aria-label="Move up" data-testid={`${name}-up-${item[idField]}`} className="p-2 text-paper/50 hover:text-paper transition-colors"><ArrowUp size={15} /></button>
                    <button onClick={() => move(item, "down")} aria-label="Move down" data-testid={`${name}-down-${item[idField]}`} className="p-2 text-paper/50 hover:text-paper transition-colors"><ArrowDown size={15} /></button>
                  </>
                )}
                <Switch checked={item.status === "published"} onCheckedChange={() => toggleStatus(item)}
                  aria-label="Toggle published" data-testid={`${name}-publish-${item[idField]}`} />
                <button onClick={() => setEditing(item)} aria-label="Edit" data-testid={`${name}-edit-${item[idField]}`} className="p-2 text-paper/50 hover:text-paper transition-colors"><Pencil size={15} /></button>
                <button onClick={() => remove(item)} aria-label="Delete" data-testid={`${name}-delete-${item[idField]}`} className="p-2 text-paper/50 hover:text-crimsonhi transition-colors"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-coal border-paper/15 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`${name}-editor`}>
          <DialogTitle className="font-display text-xl text-paper">{editing?.[idField] ? "Edit" : "New"} — {title}</DialogTitle>
          {editing && (
            <div className="mt-4">
              {fields.map((f) => (
                <Field key={f.key} label={f.label} hint={f.hint}>{renderField(f)}</Field>
              ))}
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setEditing(null)} data-testid="editor-cancel-btn"
                  className="font-ui text-xs uppercase tracking-[0.2em] text-paper/60 px-5 py-3">Cancel</button>
                <SaveBtn onClick={save} saving={saving} label="Save" tid="editor-save-btn" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
